'use client';

import { useState } from 'react';
import { useTranslation, Trans } from 'next-i18next';
import {
  passwordVisibilityIcons,
  togglePasswordVisibility,
} from '@auth/forms/formHelpers';
import { registerAction } from '@auth/actions/registerAction';
import { EMAIL_OR_PHONE_PATTERN } from '@auth/constants';
import { LinkStyled } from '@components/common';
import {
  Input, Button, Label, RadioText,
} from '@components/common/Forms';
import { BaseAuthForm } from '@auth/forms/BaseAuthForm';
import RecruiterLogosCarousel from './RecruiterLogosCarousel';

/** Types */
interface RegisterFormProps {
  locale: Locale;
  className?: string;
  type?: 'default' | 'recruiter';
}

/**
 * @description 'Register' form component.
 * - `locale`: The locale to use for translations.
 * - `className`: Optional class name to pass to the component. It will override the default class name.
 */
export const RegisterForm = (props: RegisterFormProps) => {
  // Destructure props
  const { locale, className = '', type = 'default' } = props;

  // Helpers
  const { t } = useTranslation('common');

  // Local state for wallet type checkbox; null means "not interacted yet".
  const [isWalletTypeChecked, setIsWalletTypeChecked] = useState<boolean | null>(null);

  const handleWalletTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsWalletTypeChecked(e.target.checked);
  };

  // Determines the walletType value to submit based on checkbox state and user type
  const getWalletTypeValue = (isChecked: boolean, userType: string) => {
    if (userType === 'recruiter') return isChecked ? 'personal' : 'business';

    return isChecked ? 'business' : 'personal';
  };

  // Determines what walletType value should make the checkbox appear checked
  const getExpectedCheckedValue = (userType: string) => {
    if (userType === 'recruiter') return 'personal';

    return 'business';
  };

  // Render content
  // *************************
  return (
    <BaseAuthForm
      locale={locale}
      formAction={registerAction}
      className={className}
    >
      {/* Render props from the BaseAuthForm component */}
      {({
        record,
        passwordInputType,
        setPasswordInputType,
        formState,
        isPending,
        formValuesRef,
      }) => {
        // Derive initial checked state from any restored form value; fallback by type
        const initialCheckedState = formValuesRef?.current?.walletType
          ? formValuesRef.current.walletType === getExpectedCheckedValue(type)
          : false;

        // Effective checkbox state: use user's interaction if present, else derived initial
        const effectiveChecked = isWalletTypeChecked ?? initialCheckedState;

        // Calculate the wallet type value based on effective checkbox state
        const walletTypeValue = getWalletTypeValue(effectiveChecked, type);

        return (
          <>
            {/* Email or phone */}
            <div className="relative text-left">
              <Input
                required
                type="text"
                className="asOptional"
                placeholder={t('global.label.email_or_phone')}
                pattern={`^${EMAIL_OR_PHONE_PATTERN}$`}
                patternMismatchMessage={t('auth.email.invalid-format.message')}
                {...record('email', {
                  defaultValue: formValuesRef?.current?.email || '',
                })}
              />
            </div>

            {/* Password */}
            <div className="relative text-left">
              <Input
                required
                type={passwordInputType}
                className="asOptional"
                placeholder={t('global.label.password')}
                autoComplete="new-password"
                minLength={6}
                {...record('password', {
                  defaultValue: formValuesRef?.current?.password || '',
                })}
              />

              {/* Password visibility toggle */}
              <div className="absolute right-1.5 top-2 mt-px">
                <Button
                  type="button"
                  styling="none"
                  className="p-1.5"
                  onClick={() => togglePasswordVisibility(setPasswordInputType)}
                >
                  {passwordVisibilityIcons[passwordInputType]}
                </Button>
              </div>
            </div>

            {/* Wallet type */}
            {/* Hidden input that contains the actual walletType value */}
            <Input
              type="hidden"
              {...record('walletType')}
              value={walletTypeValue}
            />
            <Label className="relative !mt-0 flex cursor-pointer items-center justify-center rounded bg-secondary/10 py-2">
              {/* Checkbox for UI only - not submitted as it's not using record function */}
              <Input
                type="checkbox"
                checked={effectiveChecked}
                onChange={handleWalletTypeChange}
              />
              <RadioText className="mt-px text-sm leading-none">
                {type === 'recruiter'
                  ? t('register.label.find.jobs')
                  : t('register.label.hire')}
              </RadioText>
            </Label>

            {/* Display a general authentication error if any */}
            {formState?.errorText && (
              <div className="mb-2 rounded bg-error/10 p-2 text-sm text-error">
                {formState.errorText}
              </div>
            )}

            {/* Register button */}
            <Button
              type="submit"
              color="secondary"
              rounding="full"
              className="mt-2 w-full !font-bold"
              isLoading={isPending}
            >
              {t('register.button')}
            </Button>

            {/* Terms info */}
            <div className="mt-3 text-sm leading-snug text-ink-medium">
              <Trans
                i18nKey="register.account.terms.info"
                components={{
                  anchor: (
                    <LinkStyled
                      styling="text"
                      href="/legal/confidentiality"
                      className="!text-sm"
                    />
                  ),
                }}
              />
            </div>

            {/* Companies using Bestjobs */}
            {type === 'recruiter' && (
              <>
                {/* Divider */}
                <div className="my-3 h-0 border-b text-center text-sm" />

                <h3 className="text-sm font-semibold">
                  {t('register.landing.companii care recruteaza')}
                </h3>
                <RecruiterLogosCarousel className="mt-3" />
              </>
            )}

            {/* Login link */}
            <div className="mt-3 border-t pt-5 text-sm">
              {t('register.logintext')}
              <LinkStyled
                styling="text"
                href="/login"
                size="sm"
                className="ml-1 font-semibold"
              >
                {t('sign_in.button')}
              </LinkStyled>
            </div>
          </>
        );
      }}
    </BaseAuthForm>
  );
};
